/**
 * components/ProjectCardMenu.tsx
 * Kebab menu with actions for a project card.
 */

import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  MoreVert,
  Edit,
  FileCopy,
  Delete,
  OpenInNew,
} from '@mui/icons-material';

interface ProjectCardMenuProps {
  projectId: string;
  projectName: string;
  projectStatus: string;
  onOpen: (projectId: string, projectType: string) => void;
  onRename: (projectId: string, projectName: string) => void;
  onDuplicate: (projectId: string) => void;
  onDelete: (projectId: string, projectName: string) => void;
}

export const ProjectCardMenu: React.FC<ProjectCardMenuProps> = ({
  projectId,
  projectStatus,
  projectName,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleMenuAction =
    (action: () => void) => (e: React.MouseEvent) => {
      e.stopPropagation();
      action();
      setAnchorEl(null);
    };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{
          color: 'var(--fx-surface-textsecondary, #64748B)',
          '&:hover': {
            backgroundColor: 'rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)',
          },
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleMenuAction(() => onOpen(projectId, projectStatus))}>
          <ListItemIcon>
            <OpenInNew fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleMenuAction(() => onRename(projectId, projectName))}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleMenuAction(() => onDuplicate(projectId))}>
          <ListItemIcon>
            <FileCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={handleMenuAction(() => onDelete(projectId, projectName))}
          sx={{
            color: '#ef4444',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: '#ef4444' }}>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
